import { Component, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';
import { GameSessionService } from '../../../core/services/game-session.service';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { TranslationService } from '../../../core/services/translation.service';
import { take } from 'rxjs/operators';
import { localizedURL } from '../../../core/helpers/navigation.helper';
import { firstValueFrom, EmptyError } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GameSession } from '../../../core/models/game.model';

@Component({
  selector: 'app-join-game-dialog',
  templateUrl: './join-game-dialog.component.html',
  styleUrls: ['./join-game-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatSlideToggleModule,
    TranslatePipe
  ]
})
export class JoinGameDialogComponent {
  // DI via inject() for consistency
  private readonly dialogRef = inject(MatDialogRef<JoinGameDialogComponent>);
  private readonly gameSessionService = inject(GameSessionService);
  private readonly translationService = inject(TranslationService);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);

  accessCode = '';
  isViewer = false;
  loading = false;

  async onJoin(): Promise<void> {
    // basic guard/normalize
    let code = this.accessCode.trim();
    // if code contains /, keep only last part
    if (code.includes('/')) {
      code = code.split('/').pop()!;
    }

    if (!code) {
      this.snackBar.open('Enter access code', 'Close', { duration: 3000 });
      return;
    }
    if (this.loading) return;
    this.loading = true;

    try {
      const join$ = (this.isViewer
        ? this.gameSessionService.joinAsViewer(code)
        : this.gameSessionService.joinSession(code)
      ).pipe(takeUntilDestroyed(this.destroyRef));

      // run both in parallel
      const [session, lang] = await Promise.all([
        firstValueFrom(join$) as Promise<GameSession>,
        firstValueFrom(this.translationService.getCurrentLang().pipe(take(1)))
      ]);

      // navigate on success
      const playareaUrl = localizedURL('/playarea/room', lang);
      this.dialogRef.close(true);
      await this.router.navigate([playareaUrl, session.id]);
    } catch (err: any) {
      // Ignore cancellation caused by component destroy
      if (err instanceof EmptyError) return;

      const message =
        err?.error?.message ??
        (this.isViewer ? 'Failed to join as viewer' : 'Failed to join game');
      console.error(message, err);
      this.snackBar.open(message, 'Close', {
        duration: 4000,
        panelClass: 'errorNotificationMessage'
      });
    } finally {
      this.loading = false;
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
